/**
 * Progress layer hooks for the web client (ISSUE-068 / ADR-0020).
 *
 * useProgress: fetches GET /me/progress — combined week ring, milestones,
 * and latest weekly win. Cache key: ['progress'].
 * useUpdateProgressPreference: PATCH /me/progress — per-user kill switch.
 */
import type { ProgressResponse } from '@nutians/shared/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';

export const PROGRESS_KEY = ['progress'] as const;

export function useProgress(enabled = true) {
  return useQuery<ProgressResponse>({
    queryKey: PROGRESS_KEY,
    queryFn: async (): Promise<ProgressResponse> => {
      const { data, error } = await api.GET('/me/progress');
      if (error || !data) throw new Error('GET /me/progress failed');
      return data as ProgressResponse;
    },
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useUpdateProgressPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (value: boolean) => {
      const { data, error } = await api.PATCH('/me/progress', {
        body: { enabled: value },
      });
      if (error || !data) throw new Error('PATCH /me/progress failed');
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PROGRESS_KEY });
    },
  });
}
